// Movie Personality Service - Computes user's movie watching patterns
// Analyzes watched movies to surface favorite directors, actors, and genres

import { Capacitor } from '@capacitor/core';
import { movieService, movieCacheService, getTmdbNumericIdForCache } from '@watchlist/shared';
import tmdbRequestQueue from './tmdbRequestQueue';
import {
  MoviePersonalityStats,
  PersonStat,
  GenreStat,
  DecadeStat,
  EnrichedMovie,
} from '../types/personality';

const CACHE_KEY_PREFIX = 'movie_personality_';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/** Minimum watched/rated titles before taste profile insights unlock (UI + aggregation). */
export const TASTE_PROFILE_MIN_WATCHED_MOVIES = 10;

const MIN_MOVIES_THRESHOLD = TASTE_PROFILE_MIN_WATCHED_MOVIES;

interface CachedPersonalityStats {
  stats: MoviePersonalityStats;
  computedAt: number;
  watchedMovieCount: number;
  /** Hash of sorted movie ids + ratings; invalidates when the watched set or scores change. */
  watchedSetFingerprint: string;
}

/**
 * Stable fingerprint for personality cache invalidation (order-independent).
 * Includes ratings because aggregates use them for averages.
 */
export function fingerprintWatchedMoviesForPersonality(
  movies: Array<{ movieId: string; rating?: number }>
): string {
  const rows = [...movies].sort((a, b) => a.movieId.localeCompare(b.movieId));
  let h = 5381;
  for (const m of rows) {
    const r = m.rating != null && !Number.isNaN(m.rating) ? String(m.rating) : '';
    for (let i = 0; i < m.movieId.length; i++) {
      h = ((h << 5) + h + m.movieId.charCodeAt(i)) >>> 0;
    }
    h = ((h << 5) + h + 58) >>> 0; // ':'
    for (let i = 0; i < r.length; i++) {
      h = ((h << 5) + h + r.charCodeAt(i)) >>> 0;
    }
    h = ((h << 5) + h + 10) >>> 0; // record separator
  }
  return `${rows.length}:${h}`;
}

/** True when we had enough watched titles but TMDB enrichment produced nothing — must not be cached or served from cache. */
function isFailedEnrichmentPersonalityCache(
  watchedMovieCount: number,
  stats: MoviePersonalityStats
): boolean {
  return watchedMovieCount >= MIN_MOVIES_THRESHOLD && stats.totalMoviesAnalyzed === 0;
}

/**
 * True when a previous run enriched far fewer titles than the watched set (timeouts / bad ids).
 * Fingerprint can still match if ratings+ids normalize the same, so we reject incomplete snapshots.
 */
function isIncompletePersonalityEnrichmentCache(
  watchedMovieCount: number,
  stats: MoviePersonalityStats
): boolean {
  if (watchedMovieCount < MIN_MOVIES_THRESHOLD) return false;
  const softFloor = Math.max(MIN_MOVIES_THRESHOLD, watchedMovieCount - 2);
  const requiredEnriched = Math.min(watchedMovieCount, softFloor);
  return stats.totalMoviesAnalyzed < requiredEnriched;
}

/**
 * Strict “drop incomplete personality cache” is Android-native only. Web + iOS keep prior behavior
 * (fingerprint + zero-enrichment check only) so we don’t invalidate good caches when a few TMDB rows fail.
 */
function shouldRejectIncompletePersonalityCache(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

class MoviePersonalityService {
  private cache: Map<string, CachedPersonalityStats> = new Map();

  /**
   * Compute personality stats for a user
   */
  async computePersonalityStats(userId: string): Promise<MoviePersonalityStats | null> {
    console.log(`🎭 Computing personality stats for user ${userId}`);

    try {
      // Get all watched movies with ratings
      // Need to pass a User-like object with uid
      console.log('🎭 [MoviePersonality] Calling movieService.getUserWatchedMovies...');

      // Add timeout for native platforms where the query might hang
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('getUserWatchedMovies timed out after 15s')), 15000);
      });

      let watchedMovies: Awaited<ReturnType<typeof movieService.getUserWatchedMovies>>;
      try {
        watchedMovies = await Promise.race([
          movieService.getUserWatchedMovies({ uid: userId } as any),
          timeoutPromise
        ]) as typeof watchedMovies;
      } catch (timeoutError) {
        console.error('🎭 [MoviePersonality] Query timed out or failed:', timeoutError);
        // Return empty stats on timeout
        return {
          topDirectors: [],
          topActors: [],
          topGenres: [],
          topDecades: [],
          totalMoviesAnalyzed: 0,
          computedAt: new Date(),
        };
      }
      console.log(`🎭 [MoviePersonality] Got ${watchedMovies.length} watched movies`);

      const slim = watchedMovies.map((m) => ({ movieId: m.movieId, rating: m.rating }));
      const fingerprint = fingerprintWatchedMoviesForPersonality(slim);
      const cached = this.getCachedStats(userId, fingerprint);
      if (cached) {
        console.log('📦 Using cached personality stats');
        return cached.stats;
      }

      // Check minimum threshold
      if (watchedMovies.length < MIN_MOVIES_THRESHOLD) {
        console.log(`⚠️ Not enough watched movies (${watchedMovies.length} < ${MIN_MOVIES_THRESHOLD})`);

        // Return partial stats with empty lists but correct count
        const partialStats: MoviePersonalityStats = {
          topDirectors: [],
          topActors: [],
          topGenres: [],
          topDecades: [],
          totalMoviesAnalyzed: watchedMovies.length,
          computedAt: new Date(),
        };

        return partialStats;
      }

      // Enrich movies with TMDB data (credits, genres)
      const enrichedMovies = await this.enrichMoviesWithTMDBData(slim);
      console.log(`✨ Enriched ${enrichedMovies.length} movies with TMDB data`);

      // Aggregate statistics
      const stats: MoviePersonalityStats = {
        topDirectors: this.aggregateDirectors(enrichedMovies),
        topActors: this.aggregateActors(enrichedMovies),
        topGenres: this.aggregateGenres(enrichedMovies),
        topDecades: this.aggregateDecades(enrichedMovies),
        totalMoviesAnalyzed: enrichedMovies.length,
        computedAt: new Date(),
      };

      if (!isFailedEnrichmentPersonalityCache(slim.length, stats)) {
        this.cacheStats(userId, stats, slim);
      } else {
        console.warn(
          '⚠️ Personality: not caching — 0 movies enriched despite enough watched; will retry next load'
        );
      }

      console.log('✅ Personality stats computed successfully');
      return stats;
    } catch (error) {
      console.error('❌ Error computing personality stats:', error);
      throw error;
    }
  }

  /**
   * Compute personality stats from pre-fetched watched movies
   * This is the preferred method for iOS/native platforms where the Firestore query might hang
   * @param userId - The user ID (for caching)
   * @param watchedMovies - Array of watched movies with movieId and optional rating
   */
  async computePersonalityStatsFromMovies(
    userId: string,
    watchedMovies: Array<{ movieId: string; rating?: number }>
  ): Promise<MoviePersonalityStats | null> {
    console.log(`🎭 Computing personality stats from ${watchedMovies.length} pre-fetched movies`);

    const fingerprint = fingerprintWatchedMoviesForPersonality(watchedMovies);
    const cached = this.getCachedStats(userId, fingerprint);
    if (cached) {
      console.log('📦 Using cached personality stats');
      return cached.stats;
    }

    try {
      // Check minimum threshold
      if (watchedMovies.length < MIN_MOVIES_THRESHOLD) {
        console.log(`⚠️ Not enough watched movies (${watchedMovies.length} < ${MIN_MOVIES_THRESHOLD})`);

        const partialStats: MoviePersonalityStats = {
          topDirectors: [],
          topActors: [],
          topGenres: [],
          topDecades: [],
          totalMoviesAnalyzed: watchedMovies.length,
          computedAt: new Date(),
        };

        return partialStats;
      }

      // Enrich movies with TMDB data (credits, genres)
      const enrichedMovies = await this.enrichMoviesWithTMDBData(watchedMovies);
      console.log(`✨ Enriched ${enrichedMovies.length} movies with TMDB data`);

      // Aggregate statistics
      const stats: MoviePersonalityStats = {
        topDirectors: this.aggregateDirectors(enrichedMovies),
        topActors: this.aggregateActors(enrichedMovies),
        topGenres: this.aggregateGenres(enrichedMovies),
        topDecades: this.aggregateDecades(enrichedMovies),
        totalMoviesAnalyzed: enrichedMovies.length,
        computedAt: new Date(),
      };

      if (!isFailedEnrichmentPersonalityCache(watchedMovies.length, stats)) {
        this.cacheStats(userId, stats, watchedMovies);
      } else {
        console.warn(
          '⚠️ Personality: not caching — 0 movies enriched despite enough watched; will retry next load'
        );
      }

      console.log('✅ Personality stats computed successfully from pre-fetched movies');
      return stats;
    } catch (error) {
      console.error('❌ Error computing personality stats from movies:', error);
      throw error;
    }
  }

  /**
   * Enrich movies with data from local cache first, then Firestore cache
   * This dramatically reduces API calls by checking client-side cache first
   */
  private async enrichMoviesWithTMDBData(
    watchedMovies: Array<{ movieId: string; rating?: number }>
  ): Promise<EnrichedMovie[]> {
    console.log(`✨ Enriching ${watchedMovies.length} movies - checking local cache first`);

    const enrichedMovies: EnrichedMovie[] = [];
    const uncachedMovieIds: string[] = [];
    const uncachedMoviesMap = new Map<string, { movieId: string; rating?: number }>();
    /** Firestore movieCache docs use numeric TMDB ids; user cache keys are often `tmdb_<n>`. */
    const uncachedByNumericTmdbId = new Map<string, { movieId: string; rating?: number }>();

    // Step 1: Check local tmdbRequestQueue cache first (no API calls!)
    for (const watchedMovie of watchedMovies) {
      try {
        // Align with saveToLocalCache / movie cache doc ids (numeric TMDB), not `tmdb_123` prefixes
        const cacheId = getTmdbNumericIdForCache(watchedMovie.movieId) ?? watchedMovie.movieId;
        const localCacheKey = `movie_${cacheId}`;
        const storedCache = localStorage.getItem('tmdb_movie_cache');

        if (storedCache) {
          const parsedCache = JSON.parse(storedCache);
          const cached = parsedCache[localCacheKey];

          if (cached && cached.data && Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) {
            const movieData = cached.data;

            // Check if we have credits data
            if (movieData.credits) {
              enrichedMovies.push({
                id: watchedMovie.movieId,
                title: movieData.title || movieData.Title || 'Unknown',
                releaseDate: movieData.release_date,
                posterPath: movieData.poster_path || null,
                userRating: watchedMovie.rating,
                credits: movieData.credits,
                genres: movieData.genres || [],
              });
              continue; // Found in local cache, skip to next movie
            }
          }
        }

        // Not in local cache or missing credits, add to uncached list
        uncachedMovieIds.push(watchedMovie.movieId);
        uncachedMoviesMap.set(watchedMovie.movieId, watchedMovie);
        const numericId = getTmdbNumericIdForCache(watchedMovie.movieId);
        if (numericId) {
          uncachedByNumericTmdbId.set(numericId, watchedMovie);
        }

      } catch (error) {
        // localStorage error, add to uncached list
        uncachedMovieIds.push(watchedMovie.movieId);
        uncachedMoviesMap.set(watchedMovie.movieId, watchedMovie);
        const numericId = getTmdbNumericIdForCache(watchedMovie.movieId);
        if (numericId) {
          uncachedByNumericTmdbId.set(numericId, watchedMovie);
        }
      }
    }

    console.log(`📦 Found ${enrichedMovies.length} movies in local cache, ${uncachedMovieIds.length} need Firestore lookup`);

    // Step 2: Get remaining movies from Firestore cache (may make API calls for uncached)
    if (uncachedMovieIds.length > 0) {
      try {
        // Add timeout to prevent Firestore hanging on native platforms
        // where the web SDK Firestore competes with native Firestore plugin
        const timeoutMs = 10000; // 10 seconds
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error(`Firestore batch cache timed out after ${timeoutMs}ms`)), timeoutMs);
        });

        const cachedMovies = await Promise.race([
          movieCacheService.getBatchMovieData(uncachedMovieIds),
          timeoutPromise
        ]);
        console.log(`📦 Retrieved ${cachedMovies.length}/${uncachedMovieIds.length} movies from Firestore cache`);

        // Also populate local cache with this data for faster future access
        this.saveToLocalCache(cachedMovies);

        for (const cached of cachedMovies) {
          const docNumeric =
            getTmdbNumericIdForCache(String(cached.id)) ?? String(cached.id);
          const watchedMovie =
            uncachedMoviesMap.get(cached.id) ??
            uncachedByNumericTmdbId.get(docNumeric) ??
            uncachedMoviesMap.get(`tmdb_${docNumeric}`);
          if (watchedMovie && cached) {
            enrichedMovies.push({
              id: watchedMovie.movieId,
              title: cached.title,
              releaseDate: cached.releaseDate,
              posterPath: cached.posterPath,
              userRating: watchedMovie.rating,
              credits: {
                crew: (cached.directors ?? []).map(d => ({
                  id: d.id,
                  name: d.name,
                  profile_path: d.profilePath,
                  job: 'Director',
                })),
                cast: (cached.mainCast ?? []).map(a => ({
                  id: a.id,
                  name: a.name,
                  profile_path: a.profilePath,
                  order: a.order,
                })),
              },
              genres: cached.genres,
            });
          }
        }
      } catch (error) {
        console.error('❌ Error fetching from Firestore cache:', error);
        // Fallback to TMDB request queue for remaining uncached movies
        console.log('⚠️ Falling back to TMDB request queue for uncached movies');
        const fallbackMovies = await this.enrichMoviesWithTMDBDataFallback(
          uncachedMovieIds.map(id => uncachedMoviesMap.get(id)!).filter(Boolean)
        );
        enrichedMovies.push(...fallbackMovies);
      }
    }

    console.log(`✅ Enriched ${enrichedMovies.length} movies total`);
    return enrichedMovies;
  }

  /**
   * Fallback method using TMDB request queue (old implementation)
   * Only used if Firestore cache fails
   */
  private async enrichMoviesWithTMDBDataFallback(
    watchedMovies: Array<{ movieId: string; rating?: number }>
  ): Promise<EnrichedMovie[]> {
    const BATCH_SIZE = 5; // Process 5 movies at a time
    const enrichedMovies: EnrichedMovie[] = [];

    for (let i = 0; i < watchedMovies.length; i += BATCH_SIZE) {
      const batch = watchedMovies.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async (movie) => {
        try {
          const tmdbData = await tmdbRequestQueue.getMovieData(movie.movieId);
          if (tmdbData) {
            return {
              id: movie.movieId,
              title: tmdbData.title || 'Unknown',
              releaseDate: tmdbData.release_date,
              posterPath: tmdbData.poster_path || null,
              userRating: movie.rating,
              credits: tmdbData.credits,
              genres: tmdbData.genres,
            } as EnrichedMovie;
          }
          return null;
        } catch (error) {
          console.error(`Failed to fetch TMDB data for movie ${movie.movieId}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(promises);
      enrichedMovies.push(...batchResults.filter((m): m is EnrichedMovie => m !== null));

      // Small delay between batches
      if (i + BATCH_SIZE < watchedMovies.length) {
        await this.sleep(100);
      }
    }

    return enrichedMovies;
  }

  /**
   * Save Firestore cache data to localStorage for faster future access
   * This ensures credits are available in local cache for future personality loads
   */
  private saveToLocalCache(movies: any[]): void {
    try {
      const storedCache = localStorage.getItem('tmdb_movie_cache');
      const cache = storedCache ? JSON.parse(storedCache) : {};
      const now = Date.now();

      for (const movie of movies) {
        const cacheKey = `movie_${movie.id}`;

        // Convert Firestore format to TMDB format with credits
        cache[cacheKey] = {
          data: {
            id: movie.id,
            title: movie.title,
            release_date: movie.releaseDate,
            poster_path: movie.posterPath,
            backdrop_path: movie.backdropPath,
            overview: movie.overview,
            genres: movie.genres,
            credits: {
              crew: movie.directors?.map((d: any) => ({
                id: d.id,
                name: d.name,
                profile_path: d.profilePath,
                job: 'Director',
              })) || [],
              cast: movie.mainCast?.map((a: any) => ({
                id: a.id,
                name: a.name,
                character: a.character,
                profile_path: a.profilePath,
                order: a.order,
              })) || [],
            },
          },
          timestamp: now,
          ttl: 24 * 60 * 60 * 1000, // 24 hours
        };
      }

      localStorage.setItem('tmdb_movie_cache', JSON.stringify(cache));
      console.log(`💾 Saved ${movies.length} movies with credits to local cache`);
    } catch (error) {
      console.warn('Failed to save to local cache:', error);
    }
  }

  /**
   * Aggregate directors from enriched movies
   */
  private aggregateDirectors(movies: EnrichedMovie[]): PersonStat[] {
    const directorMap = new Map<number, PersonStat>();

    for (const movie of movies) {
      const directors = movie.credits?.crew?.filter((c) => c.job === 'Director') || [];

      for (const director of directors) {
        const existing = directorMap.get(director.id);

        if (existing) {
          existing.movieCount++;
          if (movie.userRating) {
            existing.totalRating += movie.userRating;
          }
          existing.movieIds.push(movie.id);
          existing.averageRating = existing.totalRating / existing.movieCount;

          // Add to top movies if we have less than 3
          if (existing.topMovies.length < 3 && movie.posterPath) {
            existing.topMovies.push({
              id: movie.id,
              posterPath: movie.posterPath,
              title: movie.title,
              rating: movie.userRating,
            });
          }
        } else {
          directorMap.set(director.id, {
            id: director.id,
            name: director.name,
            profilePath: director.profile_path,
            movieCount: 1,
            totalRating: movie.userRating || 0,
            averageRating: movie.userRating || 0,
            movieIds: [movie.id],
            topMovies: movie.posterPath
              ? [{
                id: movie.id,
                posterPath: movie.posterPath,
                title: movie.title,
                rating: movie.userRating,
              }]
              : [],
          });
        }
      }
    }

    // Sort by movie count (desc), then by average rating (desc)
    const allDirectors = Array.from(directorMap.values());
    const qualifiedDirectors = allDirectors.filter((d) => d.movieCount >= 2);

    console.log(`🎬 Directors: ${allDirectors.length} total, ${qualifiedDirectors.length} with 2+ movies`);

    return qualifiedDirectors
      .sort((a, b) => {
        if (b.movieCount !== a.movieCount) return b.movieCount - a.movieCount;
        return b.averageRating - a.averageRating;
      })
      .slice(0, 10); // Top 10
  }

  /**
   * Aggregate actors from enriched movies
   */
  private aggregateActors(movies: EnrichedMovie[]): PersonStat[] {
    const actorMap = new Map<number, PersonStat>();

    for (const movie of movies) {
      // Only consider main cast (order <= 5)
      const mainCast = movie.credits?.cast?.filter((c) => c.order <= 5) || [];

      for (const actor of mainCast) {
        const existing = actorMap.get(actor.id);

        if (existing) {
          existing.movieCount++;
          if (movie.userRating) {
            existing.totalRating += movie.userRating;
          }
          existing.movieIds.push(movie.id);
          existing.averageRating = existing.totalRating / existing.movieCount;

          // Add to top movies if we have less than 3
          if (existing.topMovies.length < 3 && movie.posterPath) {
            existing.topMovies.push({
              id: movie.id,
              posterPath: movie.posterPath,
              title: movie.title,
              rating: movie.userRating,
            });
          }
        } else {
          actorMap.set(actor.id, {
            id: actor.id,
            name: actor.name,
            profilePath: actor.profile_path,
            movieCount: 1,
            totalRating: movie.userRating || 0,
            averageRating: movie.userRating || 0,
            movieIds: [movie.id],
            topMovies: movie.posterPath
              ? [{
                id: movie.id,
                posterPath: movie.posterPath,
                title: movie.title,
                rating: movie.userRating,
              }]
              : [],
          });
        }
      }
    }

    // Sort by movie count (desc), then by average rating (desc)
    return Array.from(actorMap.values())
      .filter((a) => a.movieCount >= 2) // Only show actors with 2+ movies
      .sort((a, b) => {
        if (b.movieCount !== a.movieCount) return b.movieCount - a.movieCount;
        return b.averageRating - a.averageRating;
      })
      .slice(0, 10); // Top 10
  }

  /**
   * Aggregate genres from enriched movies
   */
  private aggregateGenres(movies: EnrichedMovie[]): GenreStat[] {
    const genreMap = new Map<number, GenreStat>();

    for (const movie of movies) {
      const genres = movie.genres || [];

      for (const genre of genres) {
        const existing = genreMap.get(genre.id);

        if (existing) {
          existing.movieCount++;
          if (movie.userRating) {
            existing.totalRating += movie.userRating;
          }
          existing.averageRating = existing.totalRating / existing.movieCount;

          if (movie.posterPath && existing.topMovies.length < 200) {
            existing.topMovies.push({
              id: movie.id,
              posterPath: movie.posterPath,
              title: movie.title,
              rating: movie.userRating,
            });
          }
        } else {
          genreMap.set(genre.id, {
            id: genre.id,
            name: genre.name,
            movieCount: 1,
            totalRating: movie.userRating || 0,
            averageRating: movie.userRating || 0,
            topMovies: movie.posterPath
              ? [{
                id: movie.id,
                posterPath: movie.posterPath,
                title: movie.title,
                rating: movie.userRating,
              }]
              : [],
          });
        }
      }
    }

    // Sort by movie count (desc), then by average rating (desc)
    return Array.from(genreMap.values())
      .filter((g) => g.movieCount >= 2)
      .map((g) => ({
        ...g,
        topMovies: g.topMovies.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0)),
      }))
      .sort((a, b) => {
        if (b.movieCount !== a.movieCount) return b.movieCount - a.movieCount;
        return b.averageRating - a.averageRating;
      })
      .slice(0, 8);
  }

  /**
   * Aggregate decades from enriched movies
   */
  private aggregateDecades(movies: EnrichedMovie[]): DecadeStat[] {
    const decadeMap = new Map<string, DecadeStat>();

    for (const movie of movies) {
      if (!movie.releaseDate) continue;

      const year = parseInt(movie.releaseDate.split('-')[0]);
      if (isNaN(year)) continue;

      const decade = `${Math.floor(year / 10) * 10}s`;

      const existing = decadeMap.get(decade);

      if (existing) {
        existing.movieCount++;
        if (movie.userRating) {
          existing.totalRating += movie.userRating;
        }
        existing.averageRating = existing.totalRating / existing.movieCount;

        if (movie.posterPath && existing.topMovies.length < 200) {
          existing.topMovies.push({
            id: movie.id,
            posterPath: movie.posterPath,
            title: movie.title,
            rating: movie.userRating,
          });
        }
      } else {
        decadeMap.set(decade, {
          decade,
          movieCount: 1,
          totalRating: movie.userRating || 0,
          averageRating: movie.userRating || 0,
          topMovies: movie.posterPath
            ? [{
              id: movie.id,
              posterPath: movie.posterPath,
              title: movie.title,
              rating: movie.userRating,
            }]
            : [],
        });
      }
    }

    // Sort by movie count (desc), then by decade (desc)
    return Array.from(decadeMap.values())
      .filter((d) => d.movieCount >= 2)
      .map((d) => ({
        ...d,
        topMovies: d.topMovies.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0)),
      }))
      .sort((a, b) => {
        if (b.movieCount !== a.movieCount) return b.movieCount - a.movieCount;
        return parseInt(b.decade) - parseInt(a.decade);
      })
      .slice(0, 4);
  }

  /**
   * Get cached stats if valid TTL and fingerprint matches current watched set + ratings.
   */
  private getCachedStats(userId: string, fingerprint: string): CachedPersonalityStats | null {
    const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;

    const isUsable = (c: CachedPersonalityStats | undefined): c is CachedPersonalityStats => {
      if (!c) return false;
      if (typeof c.computedAt !== 'number' || Date.now() - c.computedAt >= CACHE_DURATION) return false;
      if (!c.watchedSetFingerprint || c.watchedSetFingerprint !== fingerprint) return false;
      if (isFailedEnrichmentPersonalityCache(c.watchedMovieCount, c.stats)) {
        console.warn(
          '🧹 Personality: removing bad cache (fingerprint matched but 0 movies enriched with enough watched)'
        );
        this.invalidateCache(userId);
        return false;
      }
      if (
        shouldRejectIncompletePersonalityCache() &&
        isIncompletePersonalityEnrichmentCache(c.watchedMovieCount, c.stats)
      ) {
        console.warn(
          '🧹 Personality: removing stale cache (Android — enriched count below minimum vs watched set)',
          { watched: c.watchedMovieCount, analyzed: c.stats.totalMoviesAnalyzed }
        );
        this.invalidateCache(userId);
        return false;
      }
      return true;
    };

    // Check memory cache
    const mem = this.cache.get(cacheKey);
    if (isUsable(mem)) {
      return mem;
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const parsed = JSON.parse(stored) as CachedPersonalityStats;
        if (!parsed.watchedSetFingerprint || parsed.watchedSetFingerprint !== fingerprint) {
          return null;
        }
        if (typeof parsed.computedAt !== 'number' || Date.now() - parsed.computedAt >= CACHE_DURATION) {
          return null;
        }
        if (isFailedEnrichmentPersonalityCache(parsed.watchedMovieCount, parsed.stats)) {
          console.warn(
            '🧹 Personality: removing bad cache from localStorage (0 enriched, enough watched)'
          );
          this.invalidateCache(userId);
          return null;
        }
        if (
          shouldRejectIncompletePersonalityCache() &&
          isIncompletePersonalityEnrichmentCache(parsed.watchedMovieCount, parsed.stats)
        ) {
          console.warn(
            '🧹 Personality: removing incomplete cache from localStorage (Android)',
            { watched: parsed.watchedMovieCount, analyzed: parsed.stats.totalMoviesAnalyzed }
          );
          this.invalidateCache(userId);
          return null;
        }
        parsed.stats.computedAt = new Date(parsed.stats.computedAt as unknown as string | number | Date);
        this.cache.set(cacheKey, parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Error reading cached stats from localStorage:', error);
    }

    return null;
  }

  /**
   * Cache stats to memory and localStorage
   */
  private cacheStats(
    userId: string,
    stats: MoviePersonalityStats,
    watchedMovies: Array<{ movieId: string; rating?: number }>
  ): void {
    const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
    const watchedSetFingerprint = fingerprintWatchedMoviesForPersonality(watchedMovies);
    const cached: CachedPersonalityStats = {
      stats,
      computedAt: Date.now(),
      watchedMovieCount: watchedMovies.length,
      watchedSetFingerprint,
    };

    // Save to memory cache
    this.cache.set(cacheKey, cached);

    // Save to localStorage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cached));
    } catch (error) {
      console.error('Error saving cached stats to localStorage:', error);
    }
  }

  /**
   * Invalidate cache for a user
   */
  invalidateCache(userId: string): void {
    const cacheKey = `${CACHE_KEY_PREFIX}${userId}`;
    this.cache.delete(cacheKey);
    try {
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error removing cached stats from localStorage:', error);
    }
  }

  /**
   * Helper: sleep function
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const moviePersonalityService = new MoviePersonalityService();
